import React, { useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ConditionalRendering } from '@common-components/conditional-rendering';
import { INELIGIBILITY_REASONS_MAP } from '@constants/individualPortal/eligibility/IneligibilityReasonsMap';
import { IStyledToggleReasonProps } from '@constants/individualPortal/eligibility/eligibility.types';
import { ROOT_STATE } from '@store/store.types';

interface IneligibilityReasonsProps {
    ineligibilityReasons: string[];
    primaryTaxFiler: string;
}

const StyledIneligibilityTitle = styled.div`
    color: ${({ theme }) => theme.gray2};
    font-weight: 600;
`;

const StyledIneligibilityReasonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const StyledIneligibilityReasons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    color: ${({ theme }) => theme.gray3};
`;

const StyledIneligibilityProgram = styled.div`
    display: flex;
    justify-content: space-between;
`;

const StyledToggleReason = styled.button<IStyledToggleReasonProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;
    border: none;
    background: none;
    font-weight: 400;
    text-align: left;
`;

const StyledReasonList = styled.ul`
    margin-left: 16px;
`;

const StyledIcon = styled(FontAwesomeIcon)`
    margin: 4px 0 0 4px;
`;

const UNKNOWN_REASON = 'Unknown reason code';
const UNKNOWN_PROGRAM = 'Unknown Program';
const INVALID_CODES = {
    VA: ['EEI03', 'EEI06', 'EEI30', 'EEI31', 'EEI102', 'EEI103', 'EEI38'],
};

export const IneligibilityReasons = (props: IneligibilityReasonsProps) => {
    const { ineligibilityReasons, primaryTaxFiler } = props;
    const [showIneligibilityReasons, setShowIneligibilityReasons] = useState({});
    const stateCode = useSelector((state: ROOT_STATE) => state.common.environment.stateCode);

    // Group reasons by program
    const groupedReasons = ineligibilityReasons.reduce((acc, code) => {
        if (INVALID_CODES[stateCode]?.includes(code)) {
            return acc;
        }

        const { program = UNKNOWN_PROGRAM, reason = `${UNKNOWN_REASON} ${code}` } =
            INELIGIBILITY_REASONS_MAP[code] || {};

        acc[program] = acc[program] || [];
        acc[program].push(reason);

        return acc;
    }, {});

    // Handle toggle show/hide
    const toggleProgram = (program: string) => {
        setShowIneligibilityReasons(prev => ({
            ...prev,
            [program]: !prev[program],
        }));
    };
    return (
        <StyledIneligibilityReasonsContainer>
            <StyledIneligibilityTitle>
                <FormattedMessage id="eligibility_results.ineligibility.header" />
            </StyledIneligibilityTitle>
            <StyledIneligibilityReasons>
                {Object.keys(groupedReasons).map(program => {
                    return (
                        <div key={program}>
                            <StyledIneligibilityProgram>
                                <FormattedMessage id={program} />
                                <StyledToggleReason onClick={() => toggleProgram(program)} className='eligibility_toggle'>
                                    <ConditionalRendering isTrue={!showIneligibilityReasons[program]}>
                                        <FormattedMessage id="eligibility_results.ineligibility.show_reasons" />
                                        <StyledIcon icon="chevron-down" />
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={showIneligibilityReasons[program]}>
                                        <FormattedMessage id="eligibility_results.ineligibility.hide_reasons" />
                                        <StyledIcon icon="chevron-up" />
                                    </ConditionalRendering>
                                </StyledToggleReason>
                            </StyledIneligibilityProgram>
                            <ConditionalRendering isTrue={showIneligibilityReasons[program]}>
                                <StyledReasonList>
                                    {groupedReasons[program].map((reason: string) => {
                                        return (
                                            <li key={reason}>
                                                <FormattedMessage
                                                    id={reason}
                                                    values={{ primaryTaxFiler, br: <br /> }}
                                                />
                                            </li>
                                        );
                                    })}
                                </StyledReasonList>
                            </ConditionalRendering>
                        </div>
                    );
                })}
            </StyledIneligibilityReasons>
        </StyledIneligibilityReasonsContainer>
    );
};
